import { apiClient } from '@/lib/api';
import type {
  HierarchyNode,
  Site,
  Zone,
  Level,
  WorkingPlaceRef,
  Tunnel,
  Team,
  HashChainBlock,
  HashChainVerificationResult,
  VerificationStats,
  MEU,
  CustodyTransferEvent,
  LotDetail,
  MEUStats,
  LotStats,
  PaginatedResponse,
  SetupStatus,
  ReefDesignation,
  TunnelStatus,
  DevelopmentPlan,
  DevelopmentPlanStatus,
  DevelopmentProgress,
  WeeklyDevelopmentSummary,
  WorkingDayLog,
  EquipmentLogEntry,
  EquipmentMeterReset,
  EquipmentSrsCategory,
  EquipmentStatus,
  EquipmentBreakdownCategory,
  EquipmentUtilizationResponse,
  EquipmentWeeklySummary,
  MaterialAvailabilityLogEntry,
  MaterialWeeklySummary,
  ProductionLogEntry,
  ProductionWeeklySummary,
  ProductionVsTarget,
  TeamLabourLogEntry,
  AbsenceReason,
  ProductionLogSubmissionStatus,
  MEUWithLocation,
  CustodyTransferEventWithOverride,
  MEULabelFormat,
  CustodyTransferType,
  HoistMethod,
  MEULocationType,
  ProductionAttachment,
  LotLineageResponse,
  TraceabilityCertificate,
  PlacementTraceability,
  EnvironmentalFeedEntry,
  ChainHeadExport,
  HashChainVerificationRun,
  IntegrityReport,
  WeeklyReportComparators,
  WeeklyMineReportModel,
  WeeklyReportDrillResult,
  ExplosiveUnit,
  LostBlastReason,
  QuantityUnit,
  MaterialClassification,
} from '@/types/operations.types';

export interface CreateSitePayload {
  name: string;
  /** FR-TRACE-001 — short uppercase code used in MEU identifiers. */
  code?: string;
  location?: string;
  description?: string;
  gpsLatitude?: number;
  gpsLongitude?: number;
  miningMethod?: string;
  primaryMineral?: string;
  licenseNumber?: string;
}

export interface CreateZonePayload {
  levelId: string;
  name: string;
  /** FR-TRACE-001 — short uppercase code used in MEU identifiers. */
  code?: string;
  classification: string;
  description?: string;
  orientation?: string;
  maxWorkerCapacity?: number;
  assignedEngineerId?: string;
}

export interface CreateLevelPayload {
  siteId: string;
  name: string;
  /** FR-TRACE-001 — short uppercase code used in MEU identifiers. */
  code?: string;
  elevationReference?: string;
  depth?: number;
  elevation?: number;
  accessType?: string;
  status?: string;
}

export interface CreateTunnelPayload {
  zoneId: string;
  name: string;
  /** FR-TRACE-001 — short uppercase code used in MEU identifiers. */
  code?: string;
  tunnelType: string;
  status?: TunnelStatus;
  plannedStrikeLength?: number;
  plannedAdvance?: number;
  currentPeriodAdvance?: number;
  reefDesignation?: ReefDesignation;
  // Deprecated — replaced by reefDesignation. Kept for the transition window.
  geologicalDesignation?: string;
}

export interface ReactivateTunnelPayload {
  reason: string;
}

// FR-OPS-014 §6.6 — production targets per Site/Zone/Level/Tunnel scope.
export interface ProductionTarget {
  id: string;
  scopeType: 'SITE' | 'ZONE' | 'LEVEL' | 'TUNNEL';
  scopeId: string;
  period: string;
  targetQuantity: number;
  unit: string;
  effectiveStart: string;
  effectiveEnd: string | null;
}

export interface CreateTeamPayload {
  tunnelId: string;
  name: string;
  shiftId: string;
  // FR-LBR-050 — payment cadence for this worker group (overrides subcontractor/tenant).
  paymentFrequency?: string;
  // Unassigned workers to add to this team on creation.
  workerIds?: string[];
}

export interface CreateMeuPayload {
  tunnelId: string;
  teamId?: string;
  extractionDate: string;
  shiftId: string;
  materialType: string;
  estimatedWeight: number;
  currentLocation?: string;
}

export interface CreateCustodyTransferPayload {
  meuId: string;
  transferType: string;
  originLocation: string;
  destinationLocation: string;
  weightAtTransfer?: number;
}

export interface CreateLotPayload {
  meuIds: string[];
  aggregationPoint: string;
  averageGrade?: number;
  processingBatchRef?: string;
  parentLotId?: string;
  mineralType?: string;
  notes?: string;
  operatorId?: string;
}

export interface SplitLotPayload {
  meuIds: string[];
  aggregationPoint?: string;
  notes?: string;
}

export interface MergeLotsPayload {
  sourceLotIds: string[];
  notes?: string;
}

export interface AddRemoveMeusPayload {
  meuIds: string[];
}

export interface CreateDevelopmentShiftPayload {
  tunnelId?: string;
  workingPlaceId?: string;
  teamId: string;
  shiftDate: string;
  shiftId: string;
  crewSize: number;
  advanceType: 'OFF_REEF' | 'ON_REEF' | 'RAISE' | 'DOWN_DIP' | 'STOPE' | 'OREPASS';
  bookedMetres: number;
  // FR-OPS-010 — tenant-configurable via `tenant.devMaterialClassifications`.
  // SRS defaults are WASTE / ORE / MIXED.
  materialClassification: string;
  measuredMetres?: number;
  notes?: string;
  // FR-DEV-002 — optional links to blasting / equipment logs.
  blastingLogId?: string;
  equipmentLogIds?: string[];
}

// FR-DEV-006 — correction always carries a reason. Other fields are optional.
export interface CorrectDevelopmentShiftPayload
  extends Partial<CreateDevelopmentShiftPayload> {
  reason: string;
}

export interface DevelopmentShiftEntry {
  id: string;
  tunnelId: string;
  workingPlaceId?: string | null;
  workingPlace?: WorkingPlaceRef | null;
  teamId: string;
  shiftDate: string;
  shiftId?: string | null;
  shift?: { id: string; name: string } | null;
  crewSize: number;
  advanceType: string;
  bookedMetres: number;
  measuredMetres?: number;
  materialClassification: string;
  notes?: string;
  contentHash: string;
  createdAt: string;
  correctsId?: string | null;
  correctionReason?: string | null;
  blastingLogId?: string | null;
  equipmentLogIds?: string[];
}

// FR-DEV-001 — Development plan payloads
export interface CreateDevelopmentPlanPayload {
  tunnelId: string;
  periodStart: string;
  periodEnd: string;
  plannedTotalMetres: number;
  plannedAdvancePerShift?: number;
  plannedWorkingDays: number;
  planReefClassification:
    | 'OFF_REEF'
    | 'ON_REEF'
    | 'RAISE'
    | 'DOWN_DIP'
    | 'STOPE'
    | 'OREPASS';
  targetCompletionDate: string;
  notes?: string;
}

export interface ApproveDevelopmentPlanPayload {
  notes?: string;
}

export interface UpsertWorkingDayPayload {
  tunnelId?: string;
  workingPlaceId?: string;
  teamId?: string;
  date: string;
  status:
    | 'WORKED'
    | 'PUBLIC_HOLIDAY'
    | 'EQUIPMENT_BREAKDOWN'
    | 'INDUSTRIAL_ACTION'
    | 'WEATHER'
    | 'OTHER';
  reasonNotes?: string;
}

export interface WeeklyDevelopmentSummaryQuery {
  weekStart: string;
  siteId?: string;
  zoneId?: string;
  levelId?: string;
  tunnelId?: string;
}

export type MisfireDisposition =
  | 'RE_BLASTED'
  | 'LEFT_IN_SITU'
  | 'REPORTED_TO_AUTHORITY';

export interface CreateShiftLogPayload {
  logType: 'PRODUCTION' | 'BLASTING';
  logDate: string;
  shiftId: string;
  tunnelId?: string;
  teamId?: string;
  notes?: string;
  // Production fields
  activityType?: string;
  oreType?: string;
  quantity?: number;
  grade?: number;
  crewSize?: number;
  // Blasting fields
  blastType?: string;
  holesDrilled?: number;
  holesCharged?: number;
  holesFired?: number;
  misfires?: number;
  explosiveType?: string;
  explosiveQuantity?: number;
  blasterName?: string;
  advanceMetres?: number;
  misfireDisposition?: MisfireDisposition;
  // FR-BLAST §6.4 additions
  workingFacePosition?: string;
  lostBlasts?: number;
  lostBlastReason?: LostBlastReason;
  blasterCertificateId?: string;
  blasterPassword?: string;
  // Ticket 13.7 — drill and blast. Powder factor is computed by the platform.
  shiftReportId?: string;
  blastCode?: string;
  workingPlaceId?: string;
  drillMetres?: number;
  avgHoleDepth?: number;
  burden?: number;
  spacing?: number;
  detonators?: number;
  tonnesBlasted?: number;
  explosiveUnit?: ExplosiveUnit;
}

export interface ShiftLogEntry {
  id: string;
  logType: 'PRODUCTION' | 'BLASTING';
  logDate: string;
  shiftId: string | null;
  tunnelId: string;
  teamId?: string;
  notes?: string;
  contentHash: string;
  createdBy: string;
  createdAt: string;
  // Production
  activityType?: string;
  oreType?: string;
  quantity?: number | null;
  grade?: number | null;
  crewSize?: number;
  // Blasting
  blastType?: string;
  holesDrilled?: number;
  holesCharged?: number;
  holesFired?: number;
  misfires?: number;
  explosiveType?: string;
  explosiveQuantity?: number | null;
  blasterName?: string;
  advanceMetres?: number | null;
  misfireDisposition?: MisfireDisposition | null;
  // FR-BLAST §6.4
  workingFacePosition?: string | null;
  lostBlasts?: number;
  lostBlastReason?: LostBlastReason | null;
  blasterCertificateId?: string | null;
  blasterId?: string | null;
  digitalSignature?: string | null;
  closedAt?: string | null;
  closedBy?: string | null;
  /**
   * §8.8 row 1 — set when a SafetyIncident was auto-opened for misfires on
   * this shift log. Use it to deep-link from the Operations UI into the
   * safety investigation.
   */
  safetyIncidentId?: string | null;
  // Ticket 13.7 — drill and blast
  siteId?: string | null;
  shiftReportId?: string | null;
  blastCode?: string | null;
  workingPlaceId?: string | null;
  workingPlace?: WorkingPlaceRef | null;
  drillMetres?: number | null;
  avgHoleDepth?: number | null;
  burden?: number | null;
  spacing?: number | null;
  detonators?: number | null;
  tonnesBlasted?: number | null;
  explosiveUnit?: ExplosiveUnit | null;
}

// FR-BLAST-004 Blaster Certificate Registry

export interface BlasterCertificate {
  id: string;
  tenantId: string;
  userId: string;
  certificateNumber: string;
  issuingAuthority: string;
  issuedAt: string;
  expiresAt: string;
  isActive: boolean;
  attachmentUrl?: string | null;
  notes?: string | null;
  revokedAt?: string | null;
  revokedReason?: string | null;
  createdAt: string;
  user?: { id: string; email?: string; firstName?: string; lastName?: string };
}

export interface CreateBlasterCertificatePayload {
  userId: string;
  certificateNumber: string;
  issuingAuthority: string;
  issuedAt: string;
  expiresAt: string;
  attachmentUrl?: string;
  notes?: string;
}

export interface UpdateBlasterCertificatePayload
  extends Partial<CreateBlasterCertificatePayload> {
  isActive?: boolean;
}

// FR-BLAST-002 weekly summary payload

export interface BlastingWeeklySummary {
  weekStart: string;
  misfireThreshold: number;
  holesPerBlastType: {
    tunnelId: string;
    tunnelName: string;
    blastType: string;
    totalHoles: number;
  }[];
  misfireCounts: {
    tunnelId: string;
    tunnelName: string;
    misfireCount: number;
    overThreshold: boolean;
  }[];
  lostBlasts: {
    tunnelId: string;
    tunnelName: string;
    reason: string;
    count: number;
  }[];
  blastEfficiency: {
    tunnelId: string;
    tunnelName: string;
    holesCharged: number;
    holesFired: number;
    efficiency: number | null;
  }[];
}

export interface WeeklyBlastingSummaryQuery {
  weekStart: string;
  siteId?: string;
  zoneId?: string;
  levelId?: string;
  tunnelId?: string;
}

export interface ShiftLogStats {
  totalProductionLogs: number;
  totalBlastingLogs: number;
  totalTonnage: number;
  avgCrewSize: number;
  totalMisfires: number;
  totalCharge: number;
}

// ── TSF & Tailings Types ──

export interface TSFCellResponse {
  id: string;
  name: string;
  location: string | null;
  designCapacity: number;
  currentVolume: number;
  percentageFull: number;
  lastSurveyDate: string | null;
  lastGeotechnicalInspectionDate: string | null;
  nextInspectionDate: string | null;
  stabilityStatus: 'STABLE' | 'UNDER_OBSERVATION' | 'RESTRICTED' | 'CLOSED';
  siteId: string;
}

export interface CreateTsfCellPayload {
  name: string;
  location?: string;
  designCapacity: number;
  siteId: string;
  nextInspectionDate?: string;
}

export interface UpdateTsfCellPayload {
  name?: string;
  location?: string;
  designCapacity?: number;
  stabilityStatus?: 'STABLE' | 'UNDER_OBSERVATION' | 'RESTRICTED' | 'CLOSED';
  lastSurveyDate?: string;
  lastGeotechnicalInspectionDate?: string;
  nextInspectionDate?: string;
}

export interface TailingsGenerationResponse {
  id: string;
  batchReference: string;
  sourceLotIds: string[];
  inputWeight: number;
  outputOreWeight: number;
  tailingsWeight: number;
  tailingsGrade: number | null;
  processingDate: string;
  operatorId: string;
  contentHash: string;
  version: number;
  createdAt: string;
}

export interface CreateTailingsGenerationPayload {
  batchReference: string;
  sourceLotIds: string[];
  inputWeight: number;
  outputOreWeight: number;
  tailingsGrade?: number;
  processingDate: string;
}

export interface TailingsPlacementResponse {
  id: string;
  generationId: string;
  placementDate: string;
  tsfCellId: string;
  volumePlaced: number;
  surveyMeasuredVolume: number | null;
  cumulativeVolumeInCell: number;
  operatorId: string;
  contentHash: string;
  version: number;
  createdAt: string;
  generation?: TailingsGenerationResponse;
  tsfCell?: TSFCellResponse;
}

export interface CreateTailingsPlacementPayload {
  generationId: string;
  placementDate: string;
  tsfCellId: string;
  volumePlaced: number;
  surveyMeasuredVolume?: number;
}

// FR-TAIL-005 / FR-TAIL-006 traceability types live in operations.types.ts —
// `PlacementTraceability` and `EnvironmentalFeedEntry`.

// ── FR-OPS-040: Dashboard Summary Types (legacy, unwired) ──

export interface TsfDashboardSummary {
  totalFacilities: number;
  totalCapacity: number;
  totalCurrentVolume: number;
  overallUtilization: number;
  cells: { id: string; name: string; percentageFull: number; stabilityStatus: string }[];
  alertCells: { id: string; name: string; percentageFull: number }[];
}

export const operationsService = {
  // ── Shift Logs ──

  async listShiftLogs(params?: {
    logType?: string;
    tunnelId?: string;
    siteId?: string;
    levelId?: string;
    zoneId?: string;
    shiftId?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<ShiftLogEntry>> {
    const { data } = await apiClient.get<PaginatedResponse<ShiftLogEntry>>('/operations/shift-logs', { params });
    return data;
  },

  async createShiftLog(payload: CreateShiftLogPayload): Promise<ShiftLogEntry> {
    const { data } = await apiClient.post<ShiftLogEntry>('/operations/shift-logs', payload);
    return data;
  },

  async getShiftLogStats(): Promise<ShiftLogStats> {
    const { data } = await apiClient.get<ShiftLogStats>('/operations/shift-logs/stats');
    return data;
  },

  async getShiftLogById(id: string): Promise<ShiftLogEntry> {
    const { data } = await apiClient.get<ShiftLogEntry>(`/operations/shift-logs/${id}`);
    return data;
  },

  // ── Hierarchy tree ──

  async getFullHierarchy(): Promise<HierarchyNode[]> {
    const { data } = await apiClient.get<HierarchyNode[]>('/operations/hierarchy');
    return data;
  },

  async getHierarchy(siteId: string): Promise<HierarchyNode> {
    const { data } = await apiClient.get<HierarchyNode>(`/operations/hierarchy/${siteId}`);
    return data;
  },

  // ── Setup Status ──

  async getSetupStatus(siteId: string): Promise<SetupStatus> {
    const { data } = await apiClient.get<SetupStatus>(`/operations/sites/${siteId}/setup-status`);
    return data;
  },

  // ── Sites ──
  async getSites(): Promise<Site[]> {
    const { data } = await apiClient.get<Site[]>('/operations/sites', {
      redirectOnError: false,
    });
    return data;
  },

  async createSite(payload: CreateSitePayload): Promise<Site> {
    const { data } = await apiClient.post<Site>('/operations/sites', payload);
    return data;
  },

  async updateSite(id: string, payload: Partial<CreateSitePayload>): Promise<Site> {
    const { data } = await apiClient.patch<Site>(`/operations/sites/${id}`, payload);
    return data;
  },

  async deleteSite(id: string) {
    await apiClient.delete(`/operations/sites/${id}`);
  },

  // ── Zones ──

  async createZone(payload: CreateZonePayload): Promise<Zone> {
    const { data } = await apiClient.post<Zone>('/operations/zones', payload);
    return data;
  },

  async updateZone(id: string, payload: Partial<CreateZonePayload>): Promise<Zone> {
    const { data } = await apiClient.patch<Zone>(`/operations/zones/${id}`, payload);
    return data;
  },

  async deleteZone(id: string) {
    await apiClient.delete(`/operations/zones/${id}`);
  },

  // ── Levels ──

  async createLevel(payload: CreateLevelPayload): Promise<Level> {
    const { data } = await apiClient.post<Level>('/operations/levels', payload);
    return data;
  },

  async updateLevel(id: string, payload: Partial<CreateLevelPayload>): Promise<Level> {
    const { data } = await apiClient.patch<Level>(`/operations/levels/${id}`, payload);
    return data;
  },

  async deleteLevel(id: string) {
    await apiClient.delete(`/operations/levels/${id}`);
  },

  // ── Tunnels ──

  async createTunnel(payload: CreateTunnelPayload): Promise<Tunnel> {
    const { data } = await apiClient.post<Tunnel>('/operations/tunnels', payload);
    return data;
  },

  async updateTunnel(id: string, payload: Partial<CreateTunnelPayload>): Promise<Tunnel> {
    const { data } = await apiClient.patch<Tunnel>(`/operations/tunnels/${id}`, payload);
    return data;
  },

  async deleteTunnel(id: string) {
    await apiClient.delete(`/operations/tunnels/${id}`);
  },

  // FR-OPS-008 — Mine Manager reactivation of a Completed/Suspended tunnel.
  async reactivateTunnel(id: string, payload: ReactivateTunnelPayload): Promise<Tunnel> {
    const { data } = await apiClient.post<Tunnel>(
      `/operations/tunnels/${id}/reactivate`,
      payload,
    );
    return data;
  },

  // ── Teams ──

  async listTeams(params?: {
    tunnelId?: string;
    shiftId?: string;
    isActive?: boolean;
  }): Promise<Team[]> {
    const { data } = await apiClient.get<Team[]>('/operations/teams', { params });
    return data;
  },

  async createTeam(payload: CreateTeamPayload): Promise<Team> {
    const { data } = await apiClient.post<Team>('/operations/teams', payload);
    return data;
  },

  async updateTeam(id: string, payload: Partial<CreateTeamPayload>): Promise<Team> {
    const { data } = await apiClient.patch<Team>(`/operations/teams/${id}`, payload);
    return data;
  },

  async deleteTeam(id: string) {
    await apiClient.delete(`/operations/teams/${id}`);
  },

  async addTeamWorkers(id: string, workerIds: string[]): Promise<void> {
    await apiClient.post(`/operations/teams/${id}/workers`, { workerIds });
  },

  async removeTeamWorker(id: string, workerId: string): Promise<void> {
    await apiClient.delete(`/operations/teams/${id}/workers/${workerId}`);
  },

  // ── MEU / Bag Tracking ──

  async listMeus(params?: {
    search?: string;
    page?: number;
    limit?: number;
    location?: string;
    materialType?: string;
    tunnelId?: string;
    shiftId?: string;
    siteId?: string;
    levelId?: string;
    zoneId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<PaginatedResponse<MEU>> {
    const { data } = await apiClient.get<PaginatedResponse<MEU>>('/operations/meus', { params });
    return data;
  },

  async getMeu(id: string): Promise<MEU> {
    const { data } = await apiClient.get<MEU>(`/operations/meus/${id}`);
    return data;
  },

  async createMeu(payload: CreateMeuPayload): Promise<MEU> {
    const { data } = await apiClient.post<MEU>('/operations/meus', payload);
    return data;
  },

  async getMeuStats(): Promise<MEUStats> {
    const { data } = await apiClient.get<MEUStats>('/operations/meus/stats');
    return data;
  },

  async getMeuCustodyChain(meuId: string): Promise<CustodyTransferEvent[]> {
    const { data } = await apiClient.get<CustodyTransferEvent[]>(`/operations/meus/${meuId}/chain`);
    return data;
  },

  async createCustodyTransfer(payload: CreateCustodyTransferPayload): Promise<CustodyTransferEvent> {
    const { data } = await apiClient.post<CustodyTransferEvent>('/operations/custody-transfers', payload);
    return data;
  },

  // ── Lots ──

  async listLots(params?: {
    search?: string;
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<PaginatedResponse<LotDetail>> {
    const { data } = await apiClient.get<PaginatedResponse<LotDetail>>('/operations/lots', { params });
    return data;
  },

  async getLot(id: string): Promise<LotDetail> {
    const { data } = await apiClient.get<LotDetail>(`/operations/lots/${id}`);
    return data;
  },

  async createLot(payload: CreateLotPayload): Promise<LotDetail> {
    const { data } = await apiClient.post<LotDetail>('/operations/lots', payload);
    return data;
  },

  async updateLotStatus(id: string, payload: { status: string; dispatchDestination?: string }): Promise<LotDetail> {
    const { data } = await apiClient.patch<LotDetail>(`/operations/lots/${id}/status`, payload);
    return data;
  },

  async getLotStats(): Promise<LotStats> {
    const { data } = await apiClient.get<LotStats>('/operations/lots/stats');
    return data;
  },

  async addMeusToLot(lotId: string, payload: AddRemoveMeusPayload): Promise<LotDetail> {
    const { data } = await apiClient.post<LotDetail>(`/operations/lots/${lotId}/meus`, payload);
    return data;
  },

  async removeMeusFromLot(lotId: string, payload: AddRemoveMeusPayload): Promise<LotDetail> {
    const { data } = await apiClient.delete<LotDetail>(`/operations/lots/${lotId}/meus`, { data: payload });
    return data;
  },

  async splitLot(lotId: string, payload: SplitLotPayload): Promise<{ original: LotDetail; newLot: LotDetail }> {
    const { data } = await apiClient.post<{ original: LotDetail; newLot: LotDetail }>(`/operations/lots/${lotId}/split`, payload);
    return data;
  },

  async mergeLots(targetLotId: string, payload: MergeLotsPayload): Promise<LotDetail> {
    const { data } = await apiClient.post<LotDetail>(`/operations/lots/${targetLotId}/merge`, payload);
    return data;
  },

  async getAvailableMeus(mineralType?: string): Promise<PaginatedResponse<MEU>> {
    const { data } = await apiClient.get<PaginatedResponse<MEU>>('/operations/meus', {
      params: { limit: 200, materialType: mineralType },
    });
    return data;
  },

  // ── Hash Chain Verification ──

  async verifyHashChain(payload: {
    tunnelId: string;
    startDate: string;
    endDate: string;
  }): Promise<HashChainVerificationResult> {
    const { data } = await apiClient.post<HashChainVerificationResult>(
      '/operations/hash-chain/verify',
      payload,
    );
    return data;
  },

  async getHashChainBlocks(params: {
    tunnelId: string;
    startDate?: string;
    endDate?: string;
  }): Promise<HashChainBlock[]> {
    const { data } = await apiClient.get<HashChainBlock[]>(
      '/operations/hash-chain/blocks',
      {
        params: {
          tunnelId: params.tunnelId,
          ...(params.startDate ? { startDate: params.startDate } : {}),
          ...(params.endDate ? { endDate: params.endDate } : {}),
        },
      },
    );
    return data;
  },

  async getVerificationStats(): Promise<VerificationStats> {
    const { data } = await apiClient.get<VerificationStats>(
      '/operations/hash-chain/stats',
    );
    return data;
  },

  // ── TSF Cells ──

  async listTsfCells(): Promise<TSFCellResponse[]> {
    const { data } = await apiClient.get<TSFCellResponse[]>('/operations/tsf-cells');
    return data;
  },

  async getTsfCell(id: string): Promise<TSFCellResponse> {
    const { data } = await apiClient.get<TSFCellResponse>(`/operations/tsf-cells/${id}`);
    return data;
  },

  async createTsfCell(payload: CreateTsfCellPayload): Promise<TSFCellResponse> {
    const { data } = await apiClient.post<TSFCellResponse>('/operations/tsf-cells', payload);
    return data;
  },

  async updateTsfCell(id: string, payload: UpdateTsfCellPayload): Promise<TSFCellResponse> {
    const { data } = await apiClient.patch<TSFCellResponse>(`/operations/tsf-cells/${id}`, payload);
    return data;
  },

  async listTsfCellPlacements(cellId: string, params?: { page?: number; limit?: number }): Promise<PaginatedResponse<TailingsPlacementResponse>> {
    const { data } = await apiClient.get<PaginatedResponse<TailingsPlacementResponse>>(
      `/operations/tsf-cells/${cellId}/placements`,
      { params },
    );
    return data;
  },

  // ── Tailings Generation ──

  async listTailingsGenerations(params?: { search?: string; batchReference?: string; page?: number; limit?: number }): Promise<PaginatedResponse<TailingsGenerationResponse>> {
    const { data } = await apiClient.get<PaginatedResponse<TailingsGenerationResponse>>(
      '/operations/tailings/generations',
      { params },
    );
    return data;
  },

  async createTailingsGeneration(payload: CreateTailingsGenerationPayload): Promise<TailingsGenerationResponse> {
    const { data } = await apiClient.post<TailingsGenerationResponse>(
      '/operations/tailings/generations',
      payload,
    );
    return data;
  },

  // ── Tailings Placement ──

  async listTailingsPlacements(params?: { search?: string; tsfCellId?: string; generationId?: string; page?: number; limit?: number }): Promise<PaginatedResponse<TailingsPlacementResponse>> {
    const { data } = await apiClient.get<PaginatedResponse<TailingsPlacementResponse>>(
      '/operations/tailings/placements',
      { params },
    );
    return data;
  },

  async createTailingsPlacement(payload: CreateTailingsPlacementPayload): Promise<{ placement: TailingsPlacementResponse; capacityWarning: boolean }> {
    const { data } = await apiClient.post<{ placement: TailingsPlacementResponse; capacityWarning: boolean }>(
      '/operations/tailings/placements',
      payload,
    );
    return data;
  },

  // ── FR-TAIL-005: Traceability ──

  async getPlacementTraceability(placementId: string): Promise<PlacementTraceability> {
    const { data } = await apiClient.get<PlacementTraceability>(
      `/operations/tailings/placements/${placementId}/traceability`,
    );
    return data;
  },

  // ── FR-TAIL-006: Environmental Feed ──

  async getTailingsEnvironmentalFeed(): Promise<{ cells: EnvironmentalFeedEntry[] }> {
    const { data } = await apiClient.get<{ cells: EnvironmentalFeedEntry[] }>(
      '/operations/tailings/environmental-feed',
    );
    return data;
  },

  // ── FR-OPS-040: Dashboard Summary ──

  async getTsfDashboardSummary(): Promise<TsfDashboardSummary> {
    const { data } = await apiClient.get<TsfDashboardSummary>(
      '/operations/tsf-cells/dashboard-summary',
    );
    return data;
  },

  // ── Weekly Mine Report (FR-WEEKLY-001..006) — canonical daily-grid model ──

  async getWeeklyMineReport(params: {
    weekStart: string;
    zoneId?: string;
    tunnelId?: string;
    levelId?: string;
    siteId?: string;
  }): Promise<WeeklyMineReportModel> {
    const { data } = await apiClient.get<WeeklyMineReportModel>(
      '/operations/weekly-report',
      { params },
    );
    return data;
  },

  // FR-WEEKLY-005 — per-cell drill-down into the underlying shift records.
  async getWeeklyReportDrill(params: {
    weekStart: string;
    date: string;
    section: string;
    metricKey: string;
    zoneId?: string;
    tunnelId?: string;
    levelId?: string;
    siteId?: string;
  }): Promise<WeeklyReportDrillResult> {
    const { data } = await apiClient.get<WeeklyReportDrillResult>(
      '/operations/weekly-report/drill',
      { params },
    );
    return data;
  },

  // ── Production Targets (FR-OPS-014) ──

  async getProductionTargets(scopeId?: string): Promise<ProductionTarget[]> {
    const { data } = await apiClient.get<ProductionTarget[]>('/operations/production-targets', { params: { scopeId } });
    return data;
  },

  async createProductionTarget(payload: { scopeType: string; scopeId: string; period: string; targetQuantity: number; unit: string; effectiveStart: string; effectiveEnd?: string }): Promise<ProductionTarget> {
    const { data } = await apiClient.post<ProductionTarget>('/operations/production-targets', payload);
    return data;
  },

  async deleteProductionTarget(id: string): Promise<unknown> {
    const { data } = await apiClient.delete<unknown>(`/operations/production-targets/${id}`);
    return data;
  },

  async getProductionVariance(params: { scopeId: string; scopeType: string; startDate: string; endDate: string }): Promise<unknown> {
    const { data } = await apiClient.get<unknown>('/operations/production-variance', { params });
    return data;
  },

  // ── Equipment Utilization (FR-EQUIP-002) ──

  async getEquipmentUtilization(tunnelId: string, startDate: string, endDate: string): Promise<unknown> {
    const { data } = await apiClient.get<unknown>('/operations/equipment-utilization', { params: { tunnelId, startDate, endDate } });
    return data;
  },

  // ── Traceability Certificate (FR-TRACE-023) ──

  async getTraceabilityCertificate(id: string, type: 'meu' | 'lot' = 'meu'): Promise<unknown> {
    const path = type === 'lot' ? `/operations/lots/${id}/certificate` : `/operations/meus/${id}/certificate`;
    const { data } = await apiClient.get<unknown>(path);
    return data;
  },

  // ── Lightweight search/lookup (id + name) ──

  async searchSites(query?: string) {
    const { data } = await apiClient.get<{ id: string; name: string }[]>('/operations/search/sites', { params: { q: query } });
    return data;
  },

  async searchZones(query?: string, siteId?: string) {
    const { data } = await apiClient.get<{ id: string; name: string; classificationType?: string }[]>('/operations/search/zones', { params: { q: query, siteId } });
    return data;
  },

  async searchTunnels(query?: string, zoneId?: string) {
    const { data } = await apiClient.get<{ id: string; name: string; tunnelType?: string; status?: string; level?: { id: string; name: string; zone?: { id: string; name: string } } }[]>('/operations/search/tunnels', { params: { q: query, zoneId } });
    return data;
  },

  async searchTeams(query?: string, tunnelId?: string) {
    const { data } = await apiClient.get<{ id: string; name: string }[]>('/operations/search/teams', { params: { q: query, tunnelId } });
    return data;
  },

  // ── Development Shifts ──

  async createDevelopmentShift(payload: CreateDevelopmentShiftPayload): Promise<DevelopmentShiftEntry> {
    const { data } = await apiClient.post<DevelopmentShiftEntry>('/operations/development-shifts', payload);
    return data;
  },

  async listDevelopmentShifts(params?: { tunnelId?: string; shiftId?: string; siteId?: string; levelId?: string; zoneId?: string; startDate?: string; endDate?: string; page?: number; limit?: number }): Promise<PaginatedResponse<DevelopmentShiftEntry>> {
    const { data } = await apiClient.get<PaginatedResponse<DevelopmentShiftEntry>>('/operations/development-shifts', { params });
    return data;
  },

  async getDevelopmentShiftById(id: string): Promise<DevelopmentShiftEntry> {
    const { data } = await apiClient.get<DevelopmentShiftEntry>(`/operations/development-shifts/${id}`);
    return data;
  },

  async correctDevelopmentShift(
    id: string,
    payload: CorrectDevelopmentShiftPayload,
  ): Promise<DevelopmentShiftEntry> {
    const { data } = await apiClient.post<DevelopmentShiftEntry>(
      `/operations/development-shifts/${id}/correct`,
      payload,
    );
    return data;
  },

  // ── FR-DEV-001 Development Plans ──

  async createDevelopmentPlan(
    payload: CreateDevelopmentPlanPayload,
  ): Promise<DevelopmentPlan> {
    const { data } = await apiClient.post<DevelopmentPlan>(
      '/operations/development-plans',
      payload,
    );
    return data;
  },

  async listDevelopmentPlans(params?: {
    tunnelId?: string;
    status?: DevelopmentPlanStatus;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<DevelopmentPlan>> {
    const { data } = await apiClient.get<PaginatedResponse<DevelopmentPlan>>(
      '/operations/development-plans',
      { params },
    );
    return data;
  },

  async getDevelopmentPlan(id: string): Promise<DevelopmentPlan> {
    const { data } = await apiClient.get<DevelopmentPlan>(
      `/operations/development-plans/${id}`,
    );
    return data;
  },

  async updateDevelopmentPlan(
    id: string,
    payload: Partial<CreateDevelopmentPlanPayload> & { status?: DevelopmentPlanStatus },
  ): Promise<DevelopmentPlan> {
    const { data } = await apiClient.patch<DevelopmentPlan>(
      `/operations/development-plans/${id}`,
      payload,
    );
    return data;
  },

  async approveDevelopmentPlan(
    id: string,
    payload: ApproveDevelopmentPlanPayload = {},
  ): Promise<DevelopmentPlan> {
    const { data } = await apiClient.post<DevelopmentPlan>(
      `/operations/development-plans/${id}/approve`,
      payload,
    );
    return data;
  },

  // ── FR-DEV-003 Cumulative progress ──

  async getDevelopmentProgress(tunnelId: string): Promise<DevelopmentProgress> {
    const { data } = await apiClient.get<DevelopmentProgress>(
      `/operations/tunnels/${tunnelId}/development-progress`,
    );
    return data;
  },

  // ── FR-DEV-005 Weekly Development Summary ──

  async getWeeklyDevelopmentSummary(
    query: WeeklyDevelopmentSummaryQuery,
  ): Promise<WeeklyDevelopmentSummary> {
    const { data } = await apiClient.get<WeeklyDevelopmentSummary>(
      '/operations/development-weekly-summary',
      { params: query },
    );
    return data;
  },

  // ── FR-DEV-005 Working Days ──

  async upsertWorkingDay(payload: UpsertWorkingDayPayload): Promise<WorkingDayLog> {
    const { data } = await apiClient.post<WorkingDayLog>(
      '/operations/working-days',
      payload,
    );
    return data;
  },

  async bulkUpsertWorkingDays(
    entries: UpsertWorkingDayPayload[],
  ): Promise<WorkingDayLog[]> {
    const { data } = await apiClient.post<WorkingDayLog[]>(
      '/operations/working-days/bulk',
      { entries },
    );
    return data;
  },

  async listWorkingDays(params?: {
    tunnelId?: string;
    workingPlaceId?: string;
    teamId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{ data: WorkingDayLog[]; total: number }> {
    const { data } = await apiClient.get<{ data: WorkingDayLog[]; total: number }>(
      '/operations/working-days',
      { params },
    );
    return data;
  },

  // ── FR-BLAST-003 shift closure + disposition ──

  async closeShiftLog(
    id: string,
    payload: { notes?: string } = {},
  ): Promise<ShiftLogEntry> {
    const { data } = await apiClient.post<ShiftLogEntry>(
      `/operations/shift-logs/${id}/close`,
      payload,
    );
    return data;
  },

  async recordMisfireDisposition(
    id: string,
    payload: { disposition: MisfireDisposition; dispositionNotes?: string },
  ): Promise<ShiftLogEntry> {
    const { data } = await apiClient.patch<ShiftLogEntry>(
      `/operations/shift-logs/${id}/misfire-disposition`,
      payload,
    );
    return data;
  },

  // ── FR-BLAST-002 weekly blasting summary ──

  async getWeeklyBlastingSummary(
    query: WeeklyBlastingSummaryQuery,
  ): Promise<BlastingWeeklySummary> {
    const { data } = await apiClient.get<BlastingWeeklySummary>(
      '/operations/blasting-weekly-summary',
      { params: query },
    );
    return data;
  },

  // ── FR-BLAST-004 Blaster Certificate Registry ──

  async createBlasterCertificate(
    payload: CreateBlasterCertificatePayload,
  ): Promise<BlasterCertificate> {
    const { data } = await apiClient.post<BlasterCertificate>(
      '/operations/blaster-certificates',
      payload,
    );
    return data;
  },

  // Uploads the scanned certificate and returns its stored URL, which the
  // caller submits as `attachmentUrl` when registering the certificate.
  async uploadBlasterCertificateAttachment(
    file: File,
  ): Promise<{ url: string; size: number; name: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await apiClient.post<{
      url: string;
      size: number;
      name: string;
    }>('/operations/blaster-certificates/attachment', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async listBlasterCertificates(params?: {
    userId?: string;
    isActive?: boolean;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<BlasterCertificate>> {
    const { data } = await apiClient.get<PaginatedResponse<BlasterCertificate>>(
      '/operations/blaster-certificates',
      { params },
    );
    return data;
  },

  async getBlasterCertificate(id: string): Promise<BlasterCertificate> {
    const { data } = await apiClient.get<BlasterCertificate>(
      `/operations/blaster-certificates/${id}`,
    );
    return data;
  },

  async updateBlasterCertificate(
    id: string,
    payload: UpdateBlasterCertificatePayload,
  ): Promise<BlasterCertificate> {
    const { data } = await apiClient.patch<BlasterCertificate>(
      `/operations/blaster-certificates/${id}`,
      payload,
    );
    return data;
  },

  async revokeBlasterCertificate(
    id: string,
    payload: { reason: string },
  ): Promise<BlasterCertificate> {
    const { data } = await apiClient.post<BlasterCertificate>(
      `/operations/blaster-certificates/${id}/revoke`,
      payload,
    );
    return data;
  },

  // ── FR-EQUIP §6.5 Equipment Logs ──

  async upsertEquipmentLog(
    payload: UpsertEquipmentLogPayload,
  ): Promise<EquipmentLogEntry & { meterJumpWarning: boolean; tonnesWarning: boolean }> {
    const { data } = await apiClient.post<
      EquipmentLogEntry & { meterJumpWarning: boolean; tonnesWarning: boolean }
    >('/operations/equipment-logs', payload);
    return data;
  },

  async recordEquipmentMeterReset(
    equipmentId: string,
    payload: { previousReading: number; newReading: number; reason: string },
  ): Promise<EquipmentMeterReset> {
    const { data } = await apiClient.post<EquipmentMeterReset>(
      `/operations/equipment/${equipmentId}/meter-resets`,
      payload,
    );
    return data;
  },

  async bulkUpsertEquipmentLogs(
    entries: UpsertEquipmentLogPayload[],
  ): Promise<EquipmentLogEntry[]> {
    const { data } = await apiClient.post<EquipmentLogEntry[]>(
      '/operations/equipment-logs/bulk',
      { entries },
    );
    return data;
  },

  async listEquipmentLogs(params?: {
    tunnelId?: string;
    siteId?: string;
    levelId?: string;
    zoneId?: string;
    equipmentId?: string;
    srsCategory?: EquipmentSrsCategory;
    shiftId?: string;
    shiftReportId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<EquipmentLogEntry>> {
    const { data } = await apiClient.get<PaginatedResponse<EquipmentLogEntry>>(
      '/operations/equipment-logs',
      { params },
    );
    return data;
  },

  async getEquipmentLogById(id: string): Promise<EquipmentLogEntry> {
    const { data } = await apiClient.get<EquipmentLogEntry>(`/operations/equipment-logs/${id}`);
    return data;
  },

  async getEquipmentUtilizationV2(params: {
    tunnelId?: string;
    equipmentId?: string;
    startDate: string;
    endDate: string;
  }): Promise<EquipmentUtilizationResponse> {
    const { data } = await apiClient.get<EquipmentUtilizationResponse>(
      '/operations/equipment-utilization',
      { params },
    );
    return data;
  },

  async getEquipmentWeeklySummary(query: {
    weekStart: string;
    siteId?: string;
    zoneId?: string;
    levelId?: string;
    tunnelId?: string;
  }): Promise<EquipmentWeeklySummary> {
    const { data } = await apiClient.get<EquipmentWeeklySummary>(
      '/operations/equipment-weekly-summary',
      { params: query },
    );
    return data;
  },

  // ── FR-EQUIP-004 Material availability ──

  async upsertMaterialAvailability(
    payload: UpsertMaterialAvailabilityPayload,
  ): Promise<MaterialAvailabilityLogEntry> {
    const { data } = await apiClient.post<MaterialAvailabilityLogEntry>(
      '/operations/material-availability',
      payload,
    );
    return data;
  },

  async listMaterialAvailability(params?: {
    tunnelId?: string;
    shiftId?: string;
    siteId?: string;
    levelId?: string;
    zoneId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<MaterialAvailabilityLogEntry>> {
    const { data } = await apiClient.get<PaginatedResponse<MaterialAvailabilityLogEntry>>(
      '/operations/material-availability',
      { params },
    );
    return data;
  },

  async getMaterialAvailabilityById(id: string): Promise<MaterialAvailabilityLogEntry> {
    const { data } = await apiClient.get<MaterialAvailabilityLogEntry>(`/operations/material-availability/${id}`);
    return data;
  },

  async getMaterialWeeklySummary(query: {
    weekStart: string;
    siteId?: string;
    zoneId?: string;
    levelId?: string;
    tunnelId?: string;
  }): Promise<MaterialWeeklySummary> {
    const { data } = await apiClient.get<MaterialWeeklySummary>(
      '/operations/material-weekly-summary',
      { params: query },
    );
    return data;
  },

  // ── FR-OPS §6.6 Production Tracking (Revised) ──

  async createProductionLog(
    payload: CreateProductionLogPayload,
  ): Promise<ProductionLogEntry> {
    const { data } = await apiClient.post<ProductionLogEntry>(
      '/operations/production-logs',
      payload,
    );
    return data;
  },

  async submitProductionLog(
    logId: string,
    payload: { licenseOverrideJustification?: string } = {},
  ): Promise<ProductionLogEntry> {
    const { data } = await apiClient.post<ProductionLogEntry>(
      `/operations/production-logs/${logId}/submit`,
      payload,
    );
    return data;
  },

  // FR-OPS-015 — bulk-submit DRAFT logs. Omit `ids` to submit all drafts.
  async submitProductionLogsBatch(
    payload: { ids?: string[]; licenseOverrideJustification?: string } = {},
  ): Promise<{ submitted: number; skipped: { id: string; reason: string }[]; total: number }> {
    const { data } = await apiClient.post<{ submitted: number; skipped: { id: string; reason: string }[]; total: number }>(
      '/operations/production-logs/submit-batch',
      payload,
    );
    return data;
  },

  async amendProductionLog(
    payload: AmendProductionLogPayload,
  ): Promise<ProductionLogEntry> {
    const { data } = await apiClient.post<ProductionLogEntry>(
      '/operations/production-logs/amend',
      payload,
    );
    return data;
  },

  async listProductionLogs(params?: {
    siteId?: string;
    zoneId?: string;
    levelId?: string;
    tunnelId?: string;
    teamId?: string;
    oreType?: string;
    submissionStatus?: ProductionLogSubmissionStatus;
    shiftId?: string;
    shiftReportId?: string;
    startDate?: string;
    endDate?: string;
    includeSuperseded?: boolean;
    page?: number;
    limit?: number;
  }): Promise<{ data: ProductionLogEntry[]; total: number; page: number; limit: number }> {
    const { data } = await apiClient.get<{
      data: ProductionLogEntry[];
      total: number;
      page: number;
      limit: number;
    }>('/operations/production-logs', { params });
    return data;
  },

  async getProductionLogById(id: string): Promise<ProductionLogEntry> {
    const { data } = await apiClient.get<ProductionLogEntry>(`/operations/production-logs/${id}`);
    return data;
  },

  async getProductionWeeklySummary(query: {
    weekStart: string;
    siteId?: string;
    zoneId?: string;
    levelId?: string;
    tunnelId?: string;
  }): Promise<ProductionWeeklySummary> {
    const { data } = await apiClient.get<ProductionWeeklySummary>(
      '/operations/production-weekly-summary',
      { params: query },
    );
    return data;
  },

  async getProductionVsTarget(query: {
    startDate: string;
    endDate: string;
    siteId?: string;
    zoneId?: string;
    levelId?: string;
    tunnelId?: string;
  }): Promise<ProductionVsTarget> {
    const { data } = await apiClient.get<ProductionVsTarget>(
      '/operations/production-vs-target',
      { params: query },
    );
    return data;
  },

  // ── FR-OPS-012 Team labour ──

  async upsertTeamLabourLog(
    payload: UpsertTeamLabourLogPayload,
  ): Promise<TeamLabourLogEntry> {
    const { data } = await apiClient.post<TeamLabourLogEntry>(
      '/operations/team-labour-logs',
      payload,
    );
    return data;
  },

  async listTeamLabourLogs(params?: {
    teamId?: string;
    tunnelId?: string;
    workingPlaceId?: string;
    shiftId?: string;
    siteId?: string;
    levelId?: string;
    zoneId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: TeamLabourLogEntry[]; total: number; page: number; limit: number }> {
    const { data } = await apiClient.get<{
      data: TeamLabourLogEntry[];
      total: number;
      page: number;
      limit: number;
    }>('/operations/team-labour-logs', { params });
    return data;
  },

  async getTeamLabourLogById(id: string): Promise<TeamLabourLogEntry> {
    const { data } = await apiClient.get<TeamLabourLogEntry>(`/operations/team-labour-logs/${id}`);
    return data;
  },

  // ── FR-TRACE §6.7 ──

  async attachMEULabel(
    meuId: string,
    payload: { labelFormat: MEULabelFormat; labelValue?: string },
  ): Promise<MEUWithLocation> {
    const { data } = await apiClient.post<MEUWithLocation>(
      `/operations/meus/${meuId}/label`,
      payload,
    );
    return data;
  },

  async recordCustodyTransferWithOverride(
    payload: CustodyTransferPayload,
  ): Promise<CustodyTransferEventWithOverride> {
    const { data } = await apiClient.post<CustodyTransferEventWithOverride>(
      '/operations/custody-transfers',
      payload,
    );
    return data;
  },

  // ── §6.7.3 Lot lineage + traceability certificate ──

  async getLotLineage(lotId: string): Promise<LotLineageResponse> {
    const { data } = await apiClient.get<LotLineageResponse>(
      `/operations/lots/${lotId}/lineage`,
    );
    return data;
  },

  async getLotCertificate(
    lotId: string,
    options: { includeWaste?: boolean } = {},
  ): Promise<TraceabilityCertificate> {
    const { data } = await apiClient.get<TraceabilityCertificate>(
      `/operations/lots/${lotId}/certificate`,
      { params: { format: 'json', ...options } },
    );
    return data;
  },

  async getMEUCertificate(
    meuId: string,
    options: { includeWaste?: boolean } = {},
  ): Promise<TraceabilityCertificate> {
    const { data } = await apiClient.get<TraceabilityCertificate>(
      `/operations/meus/${meuId}/certificate`,
      { params: { format: 'json', ...options } },
    );
    return data;
  },

  // ── §6.8 Tailings env feed (extends the existing FR-TAIL-006 method) ──

  async getTailingsEnvironmentalFeedScoped(query?: {
    startDate?: string;
    endDate?: string;
    tsfCellId?: string;
  }): Promise<{ cells: EnvironmentalFeedEntry[] }> {
    const { data } = await apiClient.get<{ cells: EnvironmentalFeedEntry[] }>(
      '/operations/tailings/environmental-feed',
      { params: query },
    );
    return data;
  },

  // ── §6.9 Chain head + verification runs ──

  async getChainHead(tunnelId: string): Promise<ChainHeadExport> {
    const { data } = await apiClient.get<ChainHeadExport>(
      `/operations/hash-chain/head/${tunnelId}`,
    );
    return data;
  },

  async getMEUChainHead(meuId: string): Promise<ChainHeadExport> {
    const { data } = await apiClient.get<ChainHeadExport>(
      `/operations/meus/${meuId}/chain-head`,
    );
    return data;
  },

  async listHashChainVerificationRuns(
    params?: { scope?: 'TUNNEL' | 'MEU' | 'TSF_CELL'; scopeId?: string },
  ): Promise<HashChainVerificationRun[]> {
    const { data } = await apiClient.get<HashChainVerificationRun[]>(
      '/operations/hash-chain/runs',
      { params },
    );
    return data;
  },

  // ── §6.9.4 Integrity Verification Report ──
  async getIntegrityReport(query: {
    tunnelId: string;
    startDate: string;
    endDate: string;
    includeMeus?: boolean;
  }): Promise<IntegrityReport> {
    const { data } = await apiClient.get<IntegrityReport>(
      '/operations/integrity-report',
      { params: query },
    );
    return data;
  },

  // ── §6.10 Weekly comparators ──
  async getWeeklyReportComparators(query: {
    weekStart: string;
    zoneId?: string;
    tunnelId?: string;
    siteId?: string;
  }): Promise<WeeklyReportComparators> {
    const { data } = await apiClient.get<WeeklyReportComparators>(
      '/operations/weekly-report/comparators',
      { params: query },
    );
    return data;
  },
};

// FR-EQUIP §6.5 payload shapes

export interface UpsertEquipmentLogPayload {
  equipmentId: string;
  tunnelId?: string;
  workingPlaceId?: string;
  logDate: string;
  shiftId: string;
  srsCategory: EquipmentSrsCategory;
  status: EquipmentStatus;
  operationalMinutes?: number;
  totalShiftMinutes?: number;
  breakdownDurationMinutes?: number;
  breakdownCategory?: EquipmentBreakdownCategory;
  breakdownReason?: string;
  pressureReadingBar?: number;
  dischargeVolumeM3?: number;
  unitsAssigned?: number;
  unitsOperational?: number;
  shiftsUsed?: number;
  lampsIssued?: number;
  lampsReturned?: number;
  notes?: string;
  // Ticket 13.1 — set when creating this log from within a shift report.
  shiftReportId?: string;
  // Ticket 13.3 — equipment hours.
  scheduledHours?: number;
  standbyHours?: number;
  meterStart?: number;
  meterEnd?: number;
  operatorId?: string;
  tonnesMoved?: number;
}

export interface UpsertMaterialAvailabilityPayload {
  tunnelId?: string;
  workingPlaceId?: string;
  logDate: string;
  shiftId: string;
  wagonPlanWaste?: number;
  wagonActualWaste?: number;
  wagonPlanReef?: number;
  wagonActualReef?: number;
  wagonSluiced?: number;
  notes?: string;
}

// FR-OPS §6.6 / §6.7 payload shapes

export interface CreateProductionLogPayload {
  teamId?: string;
  tunnelId?: string;
  workingPlaceId?: string;
  logDate: string;
  shiftId: string;
  activityType?: string;
  oreType?: string;
  quantity: number;
  subCategories: Record<string, number>;
  grade?: number;
  crewSize: number;
  equipmentUsedIds?: string[];
  attachments?: ProductionAttachment[];
  notes?: string;
  // Ticket 13.2 — a production line on the shift report.
  shiftReportId?: string;
  destination?: string;
  measureSource?: string;
  quantityUnit?: QuantityUnit;
  material?: MaterialClassification;
  materialType?: string;
  equipmentId?: string;
  payloadOverrideTonnes?: number;
}

export interface AmendProductionLogPayload {
  originalLogId: string;
  amendmentReason: string;
  patch?: CreateProductionLogPayload;
}

export interface UpsertTeamLabourLogPayload {
  teamId: string;
  tunnelId?: string;
  workingPlaceId?: string;
  logDate: string;
  shiftId: string;
  plannedHeadcount: number;
  presentWorkerIds: string[];
  absentWorkers: { workerId: string; reason: AbsenceReason; note?: string }[];
  subcontractorPresent: number;
  rdoWorkerIds: string[];
  notes?: string;
}

export interface CustodyTransferPayload {
  meuId: string;
  transferType: CustodyTransferType;
  originLocation: string;
  destinationLocation: string;
  destinationLocationType?: MEULocationType;
  weightAtTransfer?: number;
  overrideJustification?: string;
  witnessedByUserId?: string;
  // FR-TRACE-011 — per-type custody attributes.
  hoistMethod?: HoistMethod;
  recoveryPercentage?: number;
  dryingLossKg?: number;
}
